import type {
  DeletedMappingInput,
  SaveMappingsResponse,
  SavedMappingVersion,
  StageCategoriesBoardResponse,
  StageCategoriesStatsResponse,
  StageCategoryColumn,
  StageCategoryMappingInput,
  StageInfo,
} from "@shared/types/stageCategories";

import {
  CaseStageCategory,
  STAGE_CATEGORIES_IN_ORDER,
  STAGE_CATEGORY_META,
} from "@shared/constants/stageCategories";
import { buildStageKey } from "@shared/utils/stageKey";
import axios from "axios";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

import stageCategoriesApi from "@/api/admin/stageCategories";

export type StageCategoriesLoadError =
  | { code: "FEATURE_DISABLED"; message: string }
  | { code: "GENERIC"; message: string };

export type StageCategoriesSaveError =
  | { code: "CONFLICT"; message: string }
  | { code: "DUPLICATE_STAGE"; message: string; rawStage?: string }
  | { code: "GENERIC"; message: string }
  | { code: "STATEMENT_TIMEOUT"; message: string }
  | { code: "TRANSACTION_TIMEOUT"; message: string }
  | { code: "VALIDATION_ERROR"; message: string; rawStage?: string };

interface StageCategoryErrorBody {
  error?: string;
  message?: string;
  rawStage?: string;
}

function emptyBoard(): StageCategoriesBoardResponse {
  const categories = {} as Record<CaseStageCategory, StageCategoryColumn>;
  for (const cat of STAGE_CATEGORIES_IN_ORDER) {
    categories[cat] = {
      description: STAGE_CATEGORY_META[cat].description,
      label: STAGE_CATEGORY_META[cat].label,
      stages: [],
    };
  }
  return {
    categories,
    summary: { mappedCount: 0, totalMappings: 0, totalStages: 0, uncategorizedCount: 0 },
  };
}

function cloneBoard(source: StageCategoriesBoardResponse): StageCategoriesBoardResponse {
  const categories = {} as Record<CaseStageCategory, StageCategoryColumn>;
  for (const cat of STAGE_CATEGORIES_IN_ORDER) {
    const col = source.categories[cat] ?? {
      description: STAGE_CATEGORY_META[cat].description,
      label: STAGE_CATEGORY_META[cat].label,
      stages: [],
    };
    categories[cat] = {
      description: col.description,
      label: col.label,
      stages: col.stages.map((s) => ({ ...s })),
    };
  }
  return {
    categories,
    summary: { ...source.summary },
  };
}

function buildCategoryByKey(board: StageCategoriesBoardResponse): Map<string, CaseStageCategory> {
  const out = new Map<string, CaseStageCategory>();
  for (const cat of STAGE_CATEGORIES_IN_ORDER) {
    for (const stage of board.categories[cat].stages) {
      out.set(buildStageKey(stage.rawStage, stage.rawSubStage ?? ""), cat);
    }
  }
  return out;
}

/**
 * Mutate `board` in place: for every (rawStage, rawSubStage) key listed in
 * `versions`, find the matching stage anywhere on the board and overwrite
 * its `updatedAt` with the server's post-write value.
 *
 * Called after a successful save() so subsequent in-session drags carry
 * fresh clientUpdatedAt values. Skips UNCATEGORIZED stages — those are
 * case-derived (no mapping) and intentionally keep updatedAt: null.
 */
function applyMappingVersions(
  board: StageCategoriesBoardResponse,
  versions: SavedMappingVersion[]
): void {
  if (versions.length === 0) return;
  const byKey = new Map<string, string>();
  for (const v of versions) {
    byKey.set(buildStageKey(v.rawStage, v.rawSubStage), v.updatedAt);
  }
  for (const cat of STAGE_CATEGORIES_IN_ORDER) {
    if (cat === CaseStageCategory.UNCATEGORIZED) continue;
    for (const stage of board.categories[cat].stages) {
      const fresh = byKey.get(buildStageKey(stage.rawStage, stage.rawSubStage ?? ""));
      if (fresh !== undefined) stage.updatedAt = fresh;
    }
  }
}

export type SaveStatus = "error" | "saved" | "saving";

export const useStageCategoriesStore = defineStore("stageCategories", () => {
  const serverBoard = ref<null | StageCategoriesBoardResponse>(null);
  const workingBoard = ref<null | StageCategoriesBoardResponse>(null);

  // Lightweight badge counts for surfaces (e.g. sidebar) that need the
  // uncategorized count without loading the full board. Populated by
  // loadStats(); falls back to workingBoard.summary when the full board
  // is present (i.e. the admin is currently on the Case Stages page).
  const stats = ref<null | StageCategoriesStatsResponse>(null);

  const isLoading = ref(false);
  const isSaving = ref(false);
  const loadError = ref<null | StageCategoriesLoadError>(null);
  const saveError = ref<null | StageCategoriesSaveError>(null);

  // Accumulated case-update totals across a save chain. Populated as each
  // PUT in a chain completes; surfaced via `lastSaveTotals` once the chain
  // settles so the success toast can report the data effect of the action
  // (e.g. "N cases re-categorized") instead of swallowing it.
  const chainTotals = ref<{ casesMovedToUncategorized: number; casesRecategorized: number } | null>(
    null
  );
  const lastSaveTotals = ref<{
    casesMovedToUncategorized: number;
    casesRecategorized: number;
  } | null>(null);

  const board = computed(() => workingBoard.value);
  const summary = computed(() => workingBoard.value?.summary ?? null);

  /**
   * Uncategorized stage count for badge surfaces. Prefers the live working
   * board summary (kept in sync by moveStage / setCategoryStages), falls
   * back to the lightweight stats payload when the board hasn't been loaded.
   */
  const uncategorizedCount = computed<number>(() => {
    if (workingBoard.value) return workingBoard.value.summary.uncategorizedCount;
    return stats.value?.uncategorizedCount ?? 0;
  });

  /**
   * Status chip state for the header indicator.
   * - error: last save failed (stays until cleared or a save succeeds)
   * - saving: PUT is in flight
   * - saved: idle
   */
  const saveStatus = computed<SaveStatus>(() => {
    if (saveError.value) return "error";
    if (isSaving.value) return "saving";
    return "saved";
  });

  const isDirty = computed(() => {
    if (!serverBoard.value || !workingBoard.value) return false;
    const serverByKey = buildCategoryByKey(serverBoard.value);
    const workingByKey = buildCategoryByKey(workingBoard.value);
    if (serverByKey.size !== workingByKey.size) return true;
    for (const [key, cat] of workingByKey) {
      if (serverByKey.get(key) !== cat) return true;
    }
    // Also compare sortOrder within each column (drag-to-reorder counts as dirty)
    for (const cat of STAGE_CATEGORIES_IN_ORDER) {
      const serverStages = serverBoard.value.categories[cat].stages;
      const workingStages = workingBoard.value.categories[cat].stages;
      if (serverStages.length !== workingStages.length) return true;
      for (let i = 0; i < serverStages.length; i++) {
        const s = serverStages[i];
        const w = workingStages[i];
        if (s.rawStage !== w.rawStage || (s.rawSubStage ?? "") !== (w.rawSubStage ?? "")) {
          return true;
        }
      }
    }
    return false;
  });

  const dirtyCount = computed(() => {
    if (!serverBoard.value || !workingBoard.value) return 0;
    const serverByKey = buildCategoryByKey(serverBoard.value);
    const workingByKey = buildCategoryByKey(workingBoard.value);
    let count = 0;
    for (const [key, cat] of workingByKey) {
      if (serverByKey.get(key) !== cat) count++;
    }
    return count;
  });

  function findStage(
    rawStage: string,
    rawSubStage: null | string
  ): { category: CaseStageCategory; stage: StageInfo } | null {
    if (!workingBoard.value) return null;
    const targetSub = rawSubStage ?? "";
    for (const cat of STAGE_CATEGORIES_IN_ORDER) {
      const stages = workingBoard.value.categories[cat].stages;
      const stage = stages.find(
        (s) => s.rawStage === rawStage && (s.rawSubStage ?? "") === targetSub
      );
      if (stage) return { category: cat, stage };
    }
    return null;
  }

  function renumberSortOrders(category: CaseStageCategory) {
    if (!workingBoard.value) return;
    const stages = workingBoard.value.categories[category].stages;
    stages.forEach((s, i) => {
      s.sortOrder = i;
    });
  }

  // Bumped by $reset. A board or badge fetch still in flight at sign-out is the previous org's, so
  // load() and loadStats() drop their result once the generation has moved on.
  let generation = 0;

  async function load(): Promise<void> {
    const requestGeneration = generation;
    isLoading.value = true;
    loadError.value = null;
    try {
      const data = await stageCategoriesApi.fetchBoard();
      if (requestGeneration !== generation) return;
      serverBoard.value = data;
      workingBoard.value = cloneBoard(data);
    } catch (err: unknown) {
      if (requestGeneration !== generation) return;
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        loadError.value = {
          code: "FEATURE_DISABLED",
          message: "Case Stages is not enabled for this environment.",
        };
        serverBoard.value = emptyBoard();
        workingBoard.value = emptyBoard();
      } else {
        loadError.value = {
          code: "GENERIC",
          message: "Failed to load case stages. Please try again.",
        };
      }
    } finally {
      if (requestGeneration === generation) isLoading.value = false;
    }
  }

  function moveStage(
    rawStage: string,
    rawSubStage: null | string,
    toCategory: CaseStageCategory,
    toIndex: number
  ): void {
    if (!workingBoard.value) return;
    const found = findStage(rawStage, rawSubStage);
    if (!found) return;
    const { category: fromCategory } = found;

    const fromStages = workingBoard.value.categories[fromCategory].stages;
    const fromIndex = fromStages.findIndex(
      (s) => s.rawStage === rawStage && (s.rawSubStage ?? "") === (rawSubStage ?? "")
    );
    if (fromIndex === -1) return;

    const [card] = fromStages.splice(fromIndex, 1);
    const toStages = workingBoard.value.categories[toCategory].stages;
    const clampedIndex = Math.max(0, Math.min(toIndex, toStages.length));
    toStages.splice(clampedIndex, 0, card);

    renumberSortOrders(fromCategory);
    if (fromCategory !== toCategory) {
      renumberSortOrders(toCategory);
    }
    // Recompute summary.uncategorizedCount for badge accuracy
    if (workingBoard.value) {
      const unc = workingBoard.value.categories[CaseStageCategory.UNCATEGORIZED];
      workingBoard.value.summary.uncategorizedCount = unc.stages.length;
    }
  }

  function setCategoryStages(category: CaseStageCategory, stages: StageInfo[]): void {
    if (!workingBoard.value) return;
    workingBoard.value.categories[category].stages = stages;
    renumberSortOrders(category);
    if (category === CaseStageCategory.UNCATEGORIZED) {
      workingBoard.value.summary.uncategorizedCount = stages.length;
    }
  }

  function discardChanges(): void {
    if (!serverBoard.value) return;
    workingBoard.value = cloneBoard(serverBoard.value);
    saveError.value = null;
  }

  function serializeMappings(
    source: null | StageCategoriesBoardResponse = workingBoard.value,
    server: null | StageCategoriesBoardResponse = serverBoard.value
  ): StageCategoryMappingInput[] {
    if (!source) return [];
    // Derive clientUpdatedAt from serverBoard, not from the working card.
    // The card's own .updatedAt can be a stale relic across rapid drags —
    // e.g. mapped (T0) → UNC (delete in-flight) → mapped again. The card
    // still carries T0, but the server has no row, which would produce a
    // false 'created_by_other' 409 on the follow-up create. Using
    // serverBoard as the source of truth means: if serverBoard has this
    // mapping, echo its updatedAt; otherwise this is a create — omit
    // clientUpdatedAt so the backend skips the version check.
    const serverUpdatedAt = new Map<string, string>();
    if (server) {
      for (const cat of STAGE_CATEGORIES_IN_ORDER) {
        if (cat === CaseStageCategory.UNCATEGORIZED) continue;
        for (const s of server.categories[cat].stages) {
          if (s.updatedAt != null) {
            serverUpdatedAt.set(buildStageKey(s.rawStage, s.rawSubStage ?? ""), s.updatedAt);
          }
        }
      }
    }
    const out: StageCategoryMappingInput[] = [];
    for (const cat of STAGE_CATEGORIES_IN_ORDER) {
      if (cat === CaseStageCategory.UNCATEGORIZED) continue;
      const stages = source.categories[cat].stages;
      stages.forEach((s, i) => {
        const key = buildStageKey(s.rawStage, s.rawSubStage ?? "");
        out.push({
          category: cat,
          clientUpdatedAt: serverUpdatedAt.get(key),
          rawStage: s.rawStage,
          rawSubStage: s.rawSubStage ?? undefined,
          sortOrder: i,
        });
      });
    }
    return out;
  }

  /**
   * Build the explicit-deletion payload: every mapping that was on
   * `serverBoard` in a categorized column but is no longer present in
   * `workingBoard`'s categorized columns. Carries the original `updatedAt`
   * so the backend can detect delete-vs-edit races against another admin.
   *
   * A mapping dragged from a category onto UNCATEGORIZED ends up here. A
   * stage that was already UNCATEGORIZED (case-derived, no mapping) does
   * NOT — it has `updatedAt: null` and was never a mapping to begin with.
   */
  function serializeDeletedMappings(
    server: null | StageCategoriesBoardResponse = serverBoard.value,
    working: null | StageCategoriesBoardResponse = workingBoard.value
  ): DeletedMappingInput[] {
    if (!server || !working) return [];
    const workingMappedKeys = new Set<string>();
    for (const cat of STAGE_CATEGORIES_IN_ORDER) {
      if (cat === CaseStageCategory.UNCATEGORIZED) continue;
      for (const s of working.categories[cat].stages) {
        workingMappedKeys.add(buildStageKey(s.rawStage, s.rawSubStage ?? ""));
      }
    }
    const out: DeletedMappingInput[] = [];
    for (const cat of STAGE_CATEGORIES_IN_ORDER) {
      if (cat === CaseStageCategory.UNCATEGORIZED) continue;
      for (const s of server.categories[cat].stages) {
        if (s.updatedAt == null) continue; // defensive — categorized stages always carry an updatedAt
        const key = buildStageKey(s.rawStage, s.rawSubStage ?? "");
        if (workingMappedKeys.has(key)) continue;
        out.push({
          clientUpdatedAt: s.updatedAt,
          rawStage: s.rawStage,
          rawSubStage: s.rawSubStage ?? undefined,
        });
      }
    }
    return out;
  }

  /**
   * Save the current working board. Called immediately on every drop.
   *
   * Concurrency: if a PUT is already in flight when a new drop arrives, this
   * returns null — the in-flight save's completion handler re-checks isDirty
   * and triggers a follow-up save, so mid-flight drops still land. This means
   * the board handler can call save() on every drop without worrying about
   * stacking PUTs.
   *
   * Snapshot-before-PUT: we snapshot workingBoard before sending so mid-PUT
   * drops aren't clobbered when we sync serverBoard on success.
   */
  async function save(): Promise<null | SaveMappingsResponse> {
    if (!workingBoard.value || !serverBoard.value) return null;
    if (isSaving.value) return null;

    // Top-of-chain reset: a fresh save chain starts with zero accumulated
    // totals. Recursive calls below (follow-up saves on mid-PUT drops) skip
    // this reset so the chain accumulates across all its PUTs.
    if (!chainTotals.value) {
      chainTotals.value = { casesMovedToUncategorized: 0, casesRecategorized: 0 };
    }

    const snapshot = cloneBoard(workingBoard.value);
    const mappings = serializeMappings(snapshot, serverBoard.value);
    const deletedMappings = serializeDeletedMappings(serverBoard.value, snapshot);

    isSaving.value = true;
    saveError.value = null;

    const requestGeneration = generation;
    let result: null | SaveMappingsResponse = null;
    let succeeded = false;
    let conflicted = false;
    try {
      result = await stageCategoriesApi.saveMappings({ deletedMappings, mappings });
      // Signed out mid-PUT: the snapshot is the previous org's board and $reset nulled the chain.
      if (requestGeneration !== generation) return null;
      // Patch fresh server timestamps into both boards before we sync
      // serverBoard. Without this, the snapshot still carries pre-save
      // updatedAt values: newly-created mappings stay at null (next drag
      // back to UNC would ghost the deletion) and updated mappings stay at
      // the pre-write T0 (next drag would 409 with reason=modified because
      // server is now at T1). Mid-PUT drops landing in workingBoard need
      // the same patch so the chained follow-up save() carries correct
      // clientUpdatedAt values.
      applyMappingVersions(snapshot, result.mappingVersions);
      if (workingBoard.value) {
        applyMappingVersions(workingBoard.value, result.mappingVersions);
      }
      serverBoard.value = snapshot;
      succeeded = true;
      chainTotals.value.casesRecategorized += result.casesRecategorized;
      chainTotals.value.casesMovedToUncategorized += result.casesMovedToUncategorized;
      // Keep the sidebar badge in sync with the just-persisted state. Only
      // patch uncategorizedCount — we don't know the fresh fallout/excluded
      // case counts without another round trip, and synthesizing them would
      // be wrong. Skip entirely if stats was never loaded (no badge surface
      // is currently relying on it).
      if (stats.value) {
        stats.value = {
          ...stats.value,
          uncategorizedCount: snapshot.summary.uncategorizedCount,
        };
      }
    } catch (err: unknown) {
      if (requestGeneration !== generation) return null;
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const body = err.response?.data as StageCategoryErrorBody | undefined;
        const message = body?.message ?? "Failed to save case stages.";
        const code = body?.error;
        if (status === 409 || code === "CONFLICT") {
          // Optimistic-locking collision. Reload the board so the admin sees
          // the latest state; the pending drag is discarded (no auto-retry —
          // see plan: we don't want to fight a concurrent admin).
          conflicted = true;
          saveError.value = {
            code: "CONFLICT",
            message: "Another admin updated the board. Your last move was discarded.",
          };
        } else if (code === "TRANSACTION_TIMEOUT" || code === "STATEMENT_TIMEOUT") {
          saveError.value = { code, message };
        } else if (code === "VALIDATION_ERROR") {
          saveError.value = {
            code: "VALIDATION_ERROR",
            message,
            rawStage: body?.rawStage,
          };
        } else if (code === "DUPLICATE_STAGE") {
          saveError.value = {
            code: "DUPLICATE_STAGE",
            message,
            rawStage: body?.rawStage,
          };
        } else {
          saveError.value = { code: "GENERIC", message };
        }
      } else {
        saveError.value = {
          code: "GENERIC",
          message: "Unexpected error saving case stages.",
        };
      }
    } finally {
      if (requestGeneration === generation) isSaving.value = false;
    }

    // On conflict: refresh server state and abort the chain. We intentionally
    // do NOT replay any in-flight drag (the snapshot is discarded), and we
    // skip the isDirty follow-up below so we don't immediately re-collide.
    if (conflicted) {
      await load();
      lastSaveTotals.value = chainTotals.value;
      chainTotals.value = null;
      return null;
    }

    // Follow-up save: if the user dropped more cards while the PUT was in
    // flight, workingBoard has diverged from the snapshot we just persisted.
    // Chain another save so those drops commit. Only on success — on error
    // we wait for the user to retry so we don't spin retrying a doomed PUT.
    if (succeeded && isDirty.value) {
      return save();
    }

    // Chain settled (success or error). Commit accumulated totals to
    // lastSaveTotals so the success-toast watcher can read them, then
    // clear chainTotals for the next user-initiated chain.
    lastSaveTotals.value = chainTotals.value;
    chainTotals.value = null;
    return result;
  }

  /**
   * Fetch lightweight badge counts (uncategorized / fallout / excluded).
   * Safe to call from surfaces like the sidebar that need the count without
   * loading the full board. Swallows errors — a missing badge is preferable
   * to a sidebar that surfaces API failures.
   */
  async function loadStats(): Promise<void> {
    const requestGeneration = generation;
    try {
      const result = await stageCategoriesApi.fetchStats();
      if (requestGeneration === generation) stats.value = result;
    } catch {
      // Non-critical. Badge silently absent when stats fail.
    }
  }

  function clearSaveError(): void {
    saveError.value = null;
  }

  /**
   * Clear page-local board editor state. Called when the Case Stages view
   * unmounts. Intentionally preserves `stats` — that's the sidebar badge's
   * app-lifetime cache, not page state; resetting it would make the badge
   * flicker off every time the admin navigates away.
   */
  function reset(): void {
    serverBoard.value = null;
    workingBoard.value = null;
    isLoading.value = false;
    isSaving.value = false;
    loadError.value = null;
    saveError.value = null;
  }

  /**
   * Sign-out (`clearAllStores`): everything `reset()` clears, plus what it deliberately keeps for
   * the session (the sidebar badge counts and save-chain totals), since those are the last org's.
   */
  function $reset(): void {
    generation++;
    reset();
    stats.value = null;
    chainTotals.value = null;
    lastSaveTotals.value = null;
  }

  return {
    $reset,
    board,
    clearSaveError,
    dirtyCount,
    discardChanges,
    findStage,
    isDirty,
    isLoading,
    isSaving,
    lastSaveTotals,
    load,
    loadError,
    loadStats,
    moveStage,
    reset,
    save,
    saveError,
    saveStatus,
    serializeDeletedMappings,
    serializeMappings,
    serverBoard,
    setCategoryStages,
    stats,
    summary,
    uncategorizedCount,
    workingBoard,
  };
});
